"use client";

import React from "react";
import { ArrowRight, CircleQuestionMark as CircleHelp, MessageSquareText, Sparkles } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  question: string;
  snippet: string;
  detail: string;
  linkText: string;
  linkTo: string;
}
const faqs: FaqItem[] = [{
  id: "item-1",
  question: "How much does it cost to hire an SEO consultant?",
  snippet: "My SEO consultancy engagements typically range from £850 to £2,500 per month depending on website size, technical complexity, and target market.",
  detail: "Because every hour goes directly into strategic analysis and technical execution rather than overhead, your investment produces tangible search improvements. For fixed-scope projects, I also provide standalone audits and comprehensive strategy roadmaps starting from £1,200 for businesses across different markets.",
  linkText: "View affordable SEO options",
  linkTo: "/AffordableSeoServicesUkPage"
}, {
  id: "item-2",
  question: "How is working with you different from an SEO agency?",
  snippet: "You partner directly with the specialist formulating and executing your strategy, ensuring fast response times and unfiltered technical clarity.",
  detail: "Rather than navigating account managers and tiered agency layers, you collaborate one-to-one with me. This enables faster turnaround on technical recommendations, tailored commercial roadmaps, and complete transparency on what is being delivered each month.",
  linkText: "Compare with agency alternatives",
  linkTo: "/SEOAgencyAlternativeUkPage"
}, {
  id: "item-3",
  question: "How long does it take to see measurable search results?",
  snippet: "Initial technical quick-wins and indexation improvements often register within 4 to 8 weeks, while sustainable ranking and qualified organic traffic gains take 3 to 6 months.",
  detail: "Timelines depend on your website's historical authority, current technical health, and competitive landscape. I focus on high-intent terms that drive actual business enquiries rather than vanity keywords that do not convert.",
  linkText: "Explore monthly SEO services",
  linkTo: "/MonthlySeoServicesPage"
}, {
  id: "item-4",
  question: "What is included in your SEO consulting services?",
  snippet: "Every project covers technical SEO, architecture, keyword and intent mapping, on-page optimisation, commercial content strategy, and organic performance tracking.",
  detail: "Engagements also include structured schema markup, internal linking roadmaps, AI search overview optimisation, and monthly progress reviews. All technical recommendations come with ready-to-implement code snippets or direct guidance for your development team.",
  linkText: "Review technical SEO audits",
  linkTo: "/TechnicalSeoPage"
}, {
  id: "item-5",
  question: "Do you require long-term contracts?",
  snippet: "No. I operate on rolling monthly agreements and fixed-price project deliverables so you retain total flexibility.",
  detail: "Engagements run on a 30-day rolling basis with no multi-month lock-ins. I believe client relationships should be maintained through demonstrated results and clear commercial value, giving you complete control over your marketing spend.",
  linkText: "Read about monthly retainers",
  linkTo: "/MonthlySeoServicesPage"
}, {
  id: "item-6",
  question: "Can you fix drops from recent Google core updates?",
  snippet: "Yes. I start with an in-depth forensic SEO audit to pinpoint whether rank losses stem from technical crawl errors, content helpfulness signals, or link quality issues.",
  detail: "Once the root cause is mapped, I build an actionable recovery plan focusing on genuine user value, content pruning, canonical hygiene, and algorithmic alignment, typically rolling out priority fixes within the first 2 to 4 weeks.",
  linkText: "Request an SEO audit",
  linkTo: "/SeoAuditsPage"
}, {
  id: "item-7",
  question: "What do you need from me to get started?",
  snippet: "Read-only access to your Google Search Console, Google Analytics 4, and a brief 30-minute discovery conversation about your commercial priorities.",
  detail: "From there, I evaluate your existing search footprint and present an objective assessment of what needs fixing first, along with realistic milestones and an actionable 90-day roadmap.",
  linkText: "Book a consultation call",
  linkTo: "/ContactPage"
}, {
  id: "item-9",
  question: "What does an SEO consultant actually do?",
  snippet: "I diagnose organic visibility bottlenecks, architect technical and content strategies, and guide or implement actionable improvements directly.",
  detail: "My hands-on consulting covers full technical crawl audits, search intent mapping, on-page optimisation, internal linking, AI answer engine visibility, and ongoing performance reporting tied to actual revenue growth.",
  linkText: "Explore freelance SEO consulting",
  linkTo: "/FreelanceSeoConsultantPage"
}, {
  id: "item-10",
  question: "Can you help businesses outside the UK and work with international clients?",
  snippet: "Yes. I work with clients worldwide across the UK, Europe, North America, and beyond using structured asynchronous workflows and direct video sessions.",
  detail: "Whether you need international hreflang architecture, cross-border targeting, or multi-market organic growth, I adapt strategies to your regional search landscapes and collaborate smoothly across different time zones.",
  linkText: "Learn about international SEO",
  linkTo: "/InternationalSeoPage"
}];
export default function Consultantfaq() {
  return <section id="consultantfaq" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-4 inline-flex items-center gap-1.5 px-3 py-1 font-medium text-xs tracking-wide uppercase">
            <CircleHelp className="size-3.5 text-primary" />
            <span>Clear Answers</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-foreground font-default">
            Questions about hiring an independent SEO consultant worldwide
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Straightforward details on pricing, project timelines, deliverables, international collaboration, and how I work with you to grow your organic visibility.
          </p>
        </div>

        {/* FAQ Accordion List */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-2xl border border-border bg-card p-4 sm:p-8 shadow-xs">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-b border-border last:border-b-0 py-2 sm:py-3">
                <AccordionTrigger className="text-left font-medium text-base sm:text-lg hover:no-underline hover:text-primary py-3 transition-colors">
                  <span className="pr-4">{faq.question}</span>
                </AccordionTrigger>

                <AccordionContent className="text-muted-foreground leading-relaxed text-sm sm:text-base pt-1 pb-4">
                  {/* Featured Snippet block for AI / quick answers */}
                  <div className="mb-3 rounded-lg border border-border/80 bg-background/80 p-3.5 sm:p-4 text-foreground font-normal">
                    <div className="flex items-center gap-2 mb-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                      <Sparkles className="size-3.5" />
                      <span>Quick answer</span>
                    </div>
                    <p className="text-sm font-medium text-foreground">
                      {faq.snippet}
                    </p>
                  </div>

                  <p className="text-muted-foreground">{faq.detail}</p>

                  <div className="mt-3.5 pt-2 flex items-center">
                    <Link to={faq.linkTo} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-primary hover:underline group">
                      <span>{faq.linkText}</span>
                      <ArrowRight className="size-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
                    </Link>
                  </div>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {/* Bottom Conversion Prompt */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="mt-12 rounded-xl bg-muted/60 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1.5 text-center sm:text-left">
            <h3 className="text-lg font-semibold text-foreground flex items-center justify-center sm:justify-start gap-2">
              <MessageSquareText className="size-4 text-primary" />
              <span>Have a specific question about your website?</span>
            </h3>
            <p className="text-sm text-muted-foreground">
              Speak directly with me to discuss technical blockers or your search goals.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <Button asChild variant="default" size="default" className="w-full sm:w-auto">
              <Link to="/ContactPage">Book a Free SEO Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="default" className="w-full sm:w-auto">
              <Link to="/SeoAuditsPage">Get a Free SEO Audit</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}